/**
 * Studio AI - Media Generation Types
 * Supports both video and image generation
 */

export enum MediaType {
  Video = "video",
  Image = "image",
}

export enum ImageQuality {
  Low = "low",
  Medium = "medium",
  High = "high",
  /** gpt-image-2.5 only — one step above `high`. */
  XHigh = "xhigh",
  /** gpt-image-2.5 only — the most detailed (and slowest) level. */
  Max = "max",
  /** gpt-image-2.5 only — the model picks a level from the prompt. */
  Auto = "auto",
}

/**
 * One selectable image model, as served by GET /api/studio-ai/settings.
 *
 * `qualities` / `sizes` are plain strings on purpose: the backend catalog is
 * the source of truth and may list a value this frontend build doesn't know
 * yet. Consumers narrow them through `getImageModelCapabilities`, which drops
 * anything outside the `ImageQuality` enum instead of rendering a dead option.
 */
export interface StudioImageModel {
  id: string
  display_name: string
  description?: string
  family: string
  family_label: string
  tier: "fast" | "quality"
  qualities: string[]
  default_quality: string
  sizes: string[]
  supports_transparent: boolean
  /**
   * Not part of the studio contract (the endpoint only lists enabled models),
   * but tolerated so a future payload carrying the admin shape can't surface
   * a disabled deployment in the picker.
   */
  enabled?: boolean
}

/** The `image` block of GET /api/studio-ai/settings, model-picker fields only. */
export interface StudioImageSettings {
  models?: StudioImageModel[]
  default_model_id?: string
  user_selectable?: boolean
}

export enum ImageSize {
  Square1024 = "1024x1024",
  Landscape1536 = "1536x1024",
  Portrait1536 = "1024x1536",
}

export enum AspectRatio {
  Landscape = "16:9",
  Portrait = "9:16",
  Square = "1:1",
}

export enum VideoStyle {
  Custom = "custom",
  Cinematic = "cinematic",
  Documentary = "documentary",
  Animated = "animated",
  Realistic = "realistic",
  Abstract = "abstract",
  Natural = "natural",
}

export enum MediaStatus {
  Pending = "pending",
  Preprocessing = "preprocessing",
  Processing = "processing",
  Running = "running",
  Completed = "completed",
  Failed = "failed",
  Cancelled = "cancelled",
}

export interface VideoGenerationRequest {
  prompt: string
  duration?: number
  size?: string
  use_high_quality?: boolean
  quality?: string
  style?: string
  seed?: number
  project_id?: string
  remix_video_id?: string
  reference_image_url?: string
  style_template_id?: string
  style_adherence?: StyleAdherence
}

export interface ImageGenerationRequest {
  prompt: string
  size?: string
  quality?: string
  style?: string
  n?: number
  project_id?: string
  compression?: number
  output_format?: "png" | "jpeg"
  reference_image_url?: string
  background?: "opaque" | "transparent"
  style_template_id?: string
  style_adherence?: StyleAdherence
  /** Registry id of the image model to use. Ignored when the admin has
   *  turned off user selection; omitted means "use the Studio default". */
  model_id?: string
}

export interface ImageEditRequest {
  input_image: File
  prompt: string
  size?: string
  quality?: string
  compression?: number
  output_format?: "png" | "jpeg"
  input_fidelity?: "low" | "medium" | "high"
  project_id?: string
  /** See `ImageGenerationRequest.model_id`. */
  model_id?: string
}

export interface VideoGenerationResult {
  job_id: string
  status: MediaStatus
  prompt: string
  original_prompt?: string
  duration: number
  aspect_ratio: string
  quality: string
  style: string
  video_url?: string
  thumbnail_url?: string
  error_message?: string
  created_at: string
  completed_at?: string
  remixed_from_video_id?: string
  metadata?: Record<string, unknown>
}

export interface ImageGenerationResult {
  job_id: string
  status: MediaStatus
  prompt: string
  original_prompt?: string
  revised_prompt?: string
  size: string
  quality: string
  style?: string
  image_url?: string
  error_message?: string
  created_at: string
  completed_at?: string
  metadata?: Record<string, unknown>
  images?: Array<{ job_id: string; image_url?: string; revised_prompt?: string }>
}

export type StyleAdherence = "inspired" | "balanced" | "strict"

export type MediaVisibility = "personal" | "company" | "shared"

export interface MediaParticipant {
  oid: string
  displayName: string
  type: "user" | "group"
  role: "viewer" | "editor"
}

export interface Video {
  id: string
  job_id: string
  project_id?: string
  prompt: string
  original_prompt?: string
  status: MediaStatus
  duration: number
  aspect_ratio: string
  quality: string
  style: VideoStyle
  video_url?: string
  thumbnail_url?: string
  error_message?: string
  created_at: string
  completed_at?: string
  cost?: number
  remix_from_video_id?: string
  reference_image_url?: string
  metadata?: Record<string, unknown>
  media_type?: MediaType
  visibility?: MediaVisibility
  participants?: MediaParticipant[]
}

export interface Image {
  id: string
  job_id: string
  project_id?: string
  prompt: string
  original_prompt?: string
  revised_prompt?: string
  status: MediaStatus
  size: string
  quality: ImageQuality
  style?: VideoStyle
  image_url?: string
  error_message?: string
  created_at: string
  completed_at?: string
  cost?: number
  reference_image_url?: string
  compression_level?: number
  input_image_url?: string
  edit_mode?: "generate" | "edit"
  source_job_id?: string
  metadata?: Record<string, unknown>
  media_type: MediaType.Image
  visibility?: MediaVisibility
  participants?: MediaParticipant[]
}

export type MediaJob = Video | Image

export interface Project {
  id: string
  title: string
  description?: string
  user_id: string
  created_at: string
  updated_at: string
  job_ids: string[]
  active: boolean
  metadata?: Record<string, unknown>
}

export interface VideoTemplate {
  id: string
  name: string
  description: string
  category: string
  prompt_template: string
  variables: TemplateVariable[]
  default_settings: {
    duration: number
    aspect_ratio: string
    quality: string
    style: string
  }
  tags: string[]
  is_public: boolean
}

export interface TemplateVariable {
  name: string
  description: string
  required: boolean
  default_value?: string
  type: "text" | "select"
  options?: string[]
}

export interface Sora2Settings {
  duration: 4 | 8 | 12
  size: "1280x720" | "720x1280"
  use_high_quality: boolean
  quality: string
  style: VideoStyle
  seed?: number
}

export interface GenerationProgress {
  job_id: string
  status: string
  progress: number
  estimated_time?: number
}

// API Request/Response types
export type GenerateVideoRequest = VideoGenerationRequest

export interface GenerateVideoResponse {
  success: boolean
  data?: VideoGenerationResult
  error?: string
}

export interface ListProjectsResponse {
  success: boolean
  data?: Project[]
  error?: string
}

export interface ListVideosResponse {
  success: boolean
  data?: Video[]
  error?: string
}

export interface ListTemplatesResponse {
  success: boolean
  data?: VideoTemplate[]
  error?: string
}

export interface VideoStatusResponse {
  success: boolean
  data?: VideoGenerationResult
  error?: string
}

// View state for the Studio AI page
export type StudioAIView =
  | "landing"
  | "image-wizard"
  | "video-wizard"
  | "style-template-creator"
  | "image-editor"

// Style Template types
export interface StyleTemplate {
  id: string
  user_id: string
  title: string
  description: string
  prompt_fragment: string
  visibility: "personal" | "company"
  media_type: "image" | "video" | "both"
  preview_image_url?: string
  brand_colors: string[]
  participants?: MediaParticipant[]
  is_default: boolean
  recommended_adherence?: StyleAdherence
  created_at: string
  updated_at: string
}

export interface StyleTemplateDraft {
  id: string
  user_id: string
  title: string
  description: string
  prompt_fragment: string
  visibility: "personal" | "company"
  brand_colors: string[]
  chat_messages: Array<{
    id: string
    role: "user" | "assistant"
    content: string
    created_at: number
  }>
  files_meta: Array<{ name: string; size?: number; type?: string }>
  pending_ai_update?: {
    title?: string
    description?: string
    prompt_fragment?: string
    brand_colors?: string[]
    media_type?: string
    createdAt?: number
  } | null
  version: number
  created_at: string
  updated_at: string
}
